import type { SkeletonProps } from "./SkeletonTypes";

/**
 * Computes the Skeleton variant class name. Shared by both the styled-components
 * path ({@link styles.ts}) and the Tailwind path ({@link SkeletonTailwind.tsx})
 * so the two stay in lockstep.
 *
 * A "circular" skeleton is a pill-rounded square (equal height/width); anything
 * else renders the "linear" shimmer.
 */
export const getSkeletonVariant = ({
  borderRadius,
  height,
  width,
}: Pick<SkeletonProps, "borderRadius" | "height" | "width">): string =>
  borderRadius === "pill" && height === width ? "circular" : "linear";

/**
 * Maps a `borderRadius` alias/token to the corresponding theme CSS variable so
 * the Tailwind path can apply corner rounding without styled-components. Unknown
 * values (e.g. "square") fall back to no rounding.
 */
const RADIUS_VARS: Record<string, string> = {
  pill: "var(--radius-pill)",
  inner: "var(--radius-inner)",
  outer: "var(--radius-outer)",
  400: "var(--radius-400)",
  500: "var(--radius-500)",
  600: "var(--radius-600)",
  800: "var(--radius-800)",
  900: "var(--radius-900)",
  1000: "var(--radius-1000)",
};

export const radiusVar = (
  borderRadius: SkeletonProps["borderRadius"]
): string => {
  if (borderRadius == null) return "0";
  return RADIUS_VARS[String(borderRadius)] ?? "0";
};
